import * as React from 'react'
import type { AssignableRolesDto } from '@lib/dto/user.dto'
import { inviteUserInputSchema } from '@lib/input/user.input'
import { useAuth } from '@/auth/AuthContext'
import { worksAtAssignedVenues } from '@/auth/access'
import { isOrganizationAdmin } from '@/auth/roles'
import { useInviteUser } from '@/hooks/useUsers'
import { useVenues } from '@/hooks/useVenues'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { DialogFooter } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { FormError } from '@/components/ui/form-error'
import { VenueAvailabilityPicker } from '@/components/VenueAvailabilityPicker'
import { RolePicker, roleReachHint } from '@/components/people/RolePicker'
import { reachOf, roleInputOf, staffRoleKey, type PersonRoleKey } from '@/components/people/personRole'

interface PersonInviteFormProps {
  /** The company's roles, and which of them the viewer may give. */
  roles: AssignableRolesDto
  /**
   * Inviting from one venue's staff: they work there, on a role that works at
   * assigned venues, and no other venue is asked about.
   */
  venue?: { id: string; name: string }
  onDone: () => void
}

/**
 * The role an invitation starts on. An organization admin's usually invites a
 * couple, as it always has; from a venue, or for anyone else, the built-in role
 * when it can be given, else the first one that can.
 */
function startingRole(roles: AssignableRolesDto, organizationAdmin: boolean, venuesOnly: boolean): PersonRoleKey | null {
  if (organizationAdmin && !venuesOnly) return 'client'
  const giveable = roles.roles.filter((role) => role.mayGive && (!venuesOnly || role.reach === 'venues'))
  const first = giveable.find((role) => role.builtIn) ?? giveable[0]
  return first === undefined ? null : staffRoleKey(first.id)
}

/**
 * Invite someone: their name and email, the role they start on and, for a role
 * that works at assigned venues, which venues. Mounted only while its dialog is
 * open, so the fields reset each time.
 */
export function PersonInviteForm({ roles, venue, onDone }: PersonInviteFormProps) {
  const { user } = useAuth()
  const venuesQuery = useVenues()
  const invite = useInviteUser()
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [role, setRole] = React.useState<PersonRoleKey | null>(() => startingRole(roles, isOrganizationAdmin(user), venue !== undefined))
  const [venueIds, setVenueIds] = React.useState<string[]>(venue === undefined ? [] : [venue.id])
  const [error, setError] = React.useState<string | null>(null)

  const reach = reachOf(role, roles)
  // Staff on assigned venues invite to one of theirs, or they'd invite someone nobody but an organization admin could reach.
  const needsVenue = reach === 'venues' && worksAtAssignedVenues(user) && venueIds.length === 0
  const venues = (venuesQuery.data?.venues ?? []).map((v) => ({ id: v.id, name: v.name }))

  function save(): void {
    setError(null)
    if (role === null) return
    const parsed = inviteUserInputSchema.safeParse({ name, email, ...roleInputOf(role), ...(reach === 'venues' ? { venueIds } : {}) })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the fields and try again.')
      return
    }
    invite.mutate(parsed.data, { onSuccess: onDone, onError: (e) => setError(requestErrorMessage(e)) })
  }

  return (
    <>
      <div className="space-y-4">
        <Field label="Name" htmlFor="invite-name">
          <Input id="invite-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" autoFocus />
        </Field>
        <Field label="Email" htmlFor="invite-email">
          <Input id="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
        </Field>
        <Field label="Role" htmlFor="invite-role" hint={venue === undefined ? roleReachHint(role, roles) : `They’ll work at ${venue.name}.`}>
          <RolePicker id="invite-role" value={role} onChange={setRole} roles={roles} venuesOnly={venue !== undefined} />
        </Field>
        {reach === 'venues' && venue === undefined ? (
          <Field
            label="Venues they work at"
            hint={needsVenue ? 'Choose at least one of your venues.' : 'They only see and work at the venues checked here.'}
          >
            <VenueAvailabilityPicker
              venues={venues}
              value={venueIds}
              onChange={setVenueIds}
              emptyLabel="No venues chosen yet"
              summaryLabel={(n, total) => `Works at ${n} of ${total}`}
              noVenuesLabel="No venues yet. Add a venue first."
            />
          </Field>
        ) : null}
        {error ? <FormError>{error}</FormError> : null}
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={save}
          disabled={role === null || needsVenue || name.trim() === '' || email.trim() === ''}
          pending={invite.isPending}
        >
          {invite.isPending ? 'Sending…' : 'Send invite'}
        </Button>
      </DialogFooter>
    </>
  )
}
